import { fetchQuery, graphql, loadQuery } from 'react-relay';

import type { ConcreteRequest, OperationType } from 'relay-runtime';
import { JiraFilterAri } from '@atlassian/ari/jira/filter';
import { ISSUE_SEARCH_DEFAULT_COLUMNS } from '@atlassian/jira-dashboard-common';
import type { ItemProperties } from '@atlassian/jira-dashboard-user-preference';
import { JqlUtils, findClauseInAst, removeFieldClauseFromJql } from '@atlassian/jira-jql-utils';
import { MAX_COLUMNS } from '@atlassian/jira-native-issue-table';
import JiraRelayEnvironment from '@atlassian/jira-relay-environment';
import { QueryPromisesMap } from '@atlassian/jira-relay-query-promises';
import type { issueTableView_reactIssueTableGadgets_IssueTableRefetchableQuery$variables as IssueTableViewRefetchableQueryVariablesType } from '@atlassian/jira-relay/src/__generated__/issueTableView_reactIssueTableGadgets_IssueTableRefetchableQuery.graphql';
import type { loadQueryWithUserPrefs_filterResultFilterQuery as FilterResultsFilterQueryType } from '@atlassian/jira-relay/src/__generated__/loadQueryWithUserPrefs_filterResultFilterQuery.graphql';

import type { UserPrefsForIssueTableQuery } from '../../types';
import { convertFieldToJqlTerm } from './convert-field-to-jql-term';
import { getAliasesForField } from './get-aliases-for-field';
import { getSortFieldAndDirection } from './get-sort-field-and-direction';

// this is copied from NIN https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/issue-navigator/actions/common/src/utils/filters/index.tsx?at=abdc1df97c8cee2475285ab25c47a938ce99d65a
export const isFilterId = (value?: string) => value !== undefined && /^-?\d+$/.test(value);

export const transformToValidUserPrefs = (rawUserPrefs: ItemProperties) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const pageSize = parseInt(rawUserPrefs.num as string, 10);
	const columnNames =
		typeof rawUserPrefs.columnNames === 'string' ? rawUserPrefs.columnNames.split('|') : [];
	const fieldSetIds = columnNames[0] === '--Default--' ? ISSUE_SEARCH_DEFAULT_COLUMNS : columnNames;
	const stringifiedFilterId = `${rawUserPrefs.filterId}`;

	const { sortDirection, sortField } = getSortFieldAndDirection(rawUserPrefs.sortBy);
	const showResolved = !!rawUserPrefs.showResolved;
	return {
		filterId: isFilterId(stringifiedFilterId) ? stringifiedFilterId : undefined,
		columnNames: fieldSetIds,
		num: !Number.isNaN(pageSize) ? pageSize : 10,
		sortDirection,
		sortField,
		showResolved,
	};
};

export const transformToVariables = async ({
	userPrefs,
	cloudId,
	activationId,
	jql,
}: {
	userPrefs: UserPrefsForIssueTableQuery;
	cloudId: string;
	activationId: string;
	/**
	 * provide jql if we already have it (must match the filterId) or else we will fetch it
	 */
	jql?: string | null;
}): Promise<{
	variables: IssueTableViewRefetchableQueryVariablesType;
	fieldToJqlTermMap: Map<string, string>;
}> => {
	const { filterId, columnNames, num, sortField, sortDirection, showResolved } = userPrefs;

	const filterAri =
		!jql && filterId
			? JiraFilterAri.create({
					filterId,
					siteId: cloudId,
					activationId,
				}).toString()
			: '';
	const variables = {
		cloudId,
		issueSearchInput: { ...(jql ? { jql } : { filterId }) },
		after: null,
		first: num,
		fieldSetIds: columnNames,
		amountOfColumns: MAX_COLUMNS,
		shouldQueryFieldSetsById: true,
		isNotFilterFetch: !filterAri,
		filterAri,
	};
	const fieldToJqlTermMap: Map<string, string> = new Map<string, string>();
	if (!sortField || !sortDirection || !['ASC', 'DESC'].includes(sortDirection)) {
		return {
			variables,
			fieldToJqlTermMap,
		};
	}

	let jqlToModify = jql;

	if (!jqlToModify && filterAri) {
		const result = await fetchQuery<FilterResultsFilterQueryType>(
			JiraRelayEnvironment,
			graphql`
				query loadQueryWithUserPrefs_filterResultFilterQuery(
					$filterAri: ID!
					$fieldSetIds: [String!]!
					$cloudId: ID!
				) {
					jira @required(action: THROW) {
						filter(id: $filterAri) @required(action: THROW) {
							jql
						}
						fieldSetsById(fieldSetIds: $fieldSetIds, cloudId: $cloudId)
							@required(action: THROW)
							@optIn(to: "JiraFieldSetsById") {
							edges @required(action: THROW) {
								node @required(action: THROW) {
									fieldSetId @required(action: THROW)
									jqlTerm @required(action: THROW)
									aliases
									isSortable
								}
							}
						}
					}
				}
			`,
			{
				filterAri,
				fieldSetIds: variables.fieldSetIds,
				cloudId: variables.cloudId,
			},
			{
				fetchPolicy: 'store-or-network',
			},
		).toPromise();
		jqlToModify = result?.jira.filter.jql;

		if (result?.jira.fieldSetsById.edges) {
			result.jira.fieldSetsById.edges.forEach((edge) => {
				const node = edge?.node;
				if (node && node.isSortable) {
					fieldToJqlTermMap.set(node.fieldSetId, node.jqlTerm);
					node.aliases?.forEach((alias) => {
						fieldToJqlTermMap.set(alias, node.jqlTerm);
					});
				}
			});
		}
	}

	if (!jqlToModify) {
		return {
			variables,
			fieldToJqlTermMap,
		};
	}

	const jqlUtils = new JqlUtils();
	const currentSortField = convertFieldToJqlTerm(sortField);
	const jqlTermForSortField = fieldToJqlTermMap.get(currentSortField) ?? currentSortField;
	const modifiedJql = jqlUtils.jql(jqlToModify);
	const oldPrimarySortField = modifiedJql.getPrimarySortField();
	if (oldPrimarySortField) {
		// if the sort field in original filter jql is using alias, we need to pass all possible aliases in
		// so that jqlUtils knows to replace it instead of append it as a new field
		const aliases = getAliasesForField(jqlTermForSortField, fieldToJqlTermMap);
		modifiedJql.setPrimarySortField(jqlTermForSortField, sortDirection, aliases);
	} else {
		modifiedJql.setPrimarySortField(jqlTermForSortField, sortDirection, []);
	}

	let finalJql = modifiedJql.jqlString;
	if (showResolved) {
		const jast = modifiedJql.jast;
		if (jast) {
			const statusClause = findClauseInAst(jast, 'statusCategory');
			if (statusClause) {
				finalJql = removeFieldClauseFromJql(jast, statusClause);
			}
		}
	}
	return {
		variables: { ...variables, issueSearchInput: { jql: finalJql } },
		fieldToJqlTermMap,
	};
};

export async function loadQueryWithUserPrefs<T extends OperationType>(
	{ filterId, columnNames, num, sortField, sortDirection }: UserPrefsForIssueTableQuery,
	query: ConcreteRequest,
	cloudId: string,
	activationId: string,
	fetchPolicy: 'store-only' | 'store-and-network' | 'store-or-network' = 'store-or-network',
	jql?: string,
) {
	const { variables } = await transformToVariables({
		userPrefs: { filterId, columnNames, num, sortField, sortDirection },
		cloudId,
		activationId,
		jql,
	});

	const queryReference = loadQuery<T>(JiraRelayEnvironment, query, variables, {
		fetchPolicy,
	});

	if (__SERVER__ && queryReference.id != null) {
		await QueryPromisesMap.get(queryReference.id);
	}

	return queryReference;
}
